import { useState } from 'react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, Eye, EyeOff, BarChart3, TrendingUp, Shield, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

export default function AdminLogin({ onLoginSuccess, onNavigateToSignup }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/authentication/login`, {
        email: email.toLowerCase(),
        password,
      });

      if (response.data && response.data.token) {
        if (response.data.user.user_type !== 'owner') {
          setError('Access denied. Only organization owners can access Insights.');
          return;
        }

        localStorage.setItem('admin_token', response.data.token);
        localStorage.setItem('supabase_access_token', response.data.token);
        if (response.data.refreshToken) {
          localStorage.setItem('refresh_token', response.data.refreshToken);
        }
        localStorage.setItem('user_id', response.data.user.id);
        localStorage.setItem('admin_email', email);
        localStorage.setItem('user_type', response.data.user.user_type);
        localStorage.setItem('full_name', response.data.user.fullName);
        const ownedOrg = response.data.user.allOrganizations?.find(o => o.isOwned);
        if (ownedOrg?.organizationName) {
          localStorage.setItem('org_name', ownedOrg.organizationName);
        }

        const expiryTime = Date.now() + (7 * 24 * 60 * 60 * 1000);
        localStorage.setItem('login_expiry_at', expiryTime.toString());

        onLoginSuccess(response.data.user);
      } else {
        setError('Invalid response from server. Please try again.');
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || 'Invalid email or password.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-2/5 flex-col justify-between p-12 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #0c1a3a 0%, #091229 60%, #0c1a3a 100%)' }}>
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Large primary glow — top right */}
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, #2563eb55 0%, #2563eb18 45%, transparent 70%)' }} />
          {/* Medium glow — bottom left */}
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full" style={{ background: 'radial-gradient(circle, #2563eb40 0%, #2563eb12 50%, transparent 70%)' }} />
          {/* Small accent circle — mid right */}
          <div className="absolute top-1/2 -right-10 w-40 h-40 rounded-full" style={{ background: 'radial-gradient(circle, #2563eb30 0%, transparent 65%)' }} />
          {/* Subtle grid overlay */}
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(#2563eb0a 1px, transparent 1px), linear-gradient(90deg, #2563eb0a 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          {/* Horizontal accent line */}
          <div className="absolute top-1/3 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right, transparent, #2563eb40, transparent)' }} />
        </div>

        <div className="relative z-10">
          <img src="./images/Evolvv-main.png" alt="Evolvv" className="h-8 object-contain brightness-0 invert" />
        </div>

        <div className="relative z-10 space-y-8">
          <div>
            <div className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full mb-5" style={{ background: '#2563eb25', color: '#93c5fd', border: '1px solid #2563eb60' }}>
              <BarChart3 className="h-4 w-4" style={{ color: '#2563eb' }} />
              Insights Dashboard
            </div>
            <h1 className="text-4xl font-bold text-white leading-tight">
              See how your space is performing
            </h1>
            <p className="text-base mt-4 leading-relaxed" style={{ color: '#94a3b8' }}>
              Track organization health, payment behaviour, and engagement across all the companies in your coworking space.
            </p>
          </div>

          <div className="space-y-4">
            {[
              { icon: Building2, title: 'Organization Overview', desc: 'Monitor all member companies in one place' },
              { icon: TrendingUp, title: 'Health Scoring', desc: 'Automated payment and engagement metrics' },
              { icon: Shield, title: 'Secure Access', desc: 'Private dashboard for space operators only' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-4">
                <div className="mt-0.5 w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#2563eb25', border: '1px solid #2563eb50' }}>
                  <Icon className="h-5 w-5" style={{ color: '#2563eb' }} />
                </div>
                <div>
                  <div className="text-base font-semibold text-white">{title}</div>
                  <div className="text-sm mt-0.5" style={{ color: '#64748b' }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-xs" style={{ color: '#334155' }}>Powered by Evolvv · All rights reserved</p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden mb-10 flex justify-center">
            <img src="./images/Evolvv-main.png" alt="Evolvv" className="h-8 object-contain" />
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900">Sign in</h2>
            <p className="text-slate-500 text-sm mt-1">Access your Insights dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Email address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-9 h-10 border-slate-200 focus:border-[#2563eb] focus:ring-[#2563eb] text-sm"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-9 pr-10 h-10 border-slate-200 focus:border-[#2563eb] focus:ring-[#2563eb] text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-10 text-white font-medium text-sm rounded-lg transition-colors disabled:opacity-50" style={{ background: '#2563eb' }} onMouseEnter={e => e.currentTarget.style.background='#1d4ed8'} onMouseLeave={e => e.currentTarget.style.background='#2563eb'}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Don't have an account?{' '}
            <button
              onClick={onNavigateToSignup}
              className="font-semibold transition-colors" style={{ color: '#2563eb' }} onMouseEnter={e => e.currentTarget.style.color='#1d4ed8'} onMouseLeave={e => e.currentTarget.style.color='#2563eb'}
            >
              Create one
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
